<template>
  <div>
    <div v-if="tableTips && tableTips.length" class="cb-tips">
      <p v-for="(tip, index) in tableTips" :key="index" class="cb-tip-item">{{ tip }}</p>
    </div>

    <div class="table-container">
      <div v-if="showMarketFilter" class="filter-bar">
        <el-input
          v-model="filterKeyword"
          clearable
          size="small"
          placeholder="搜索名称或代码"
          prefix-icon="el-icon-search"
          class="filter-keyword"
        />
        <div v-if="tab === 'lof'" class="filter-group">
          <span class="filter-label">申购</span>
          <el-checkbox-group v-model="filterTagTypes" size="small" class="filter-checks">
            <el-checkbox label="limit">限额</el-checkbox>
            <el-checkbox label="open">开放</el-checkbox>
            <el-checkbox label="pause">暂停</el-checkbox>
          </el-checkbox-group>
        </div>
        <div class="filter-group">
          <span class="filter-label">溢价</span>
          <el-radio-group v-model="filterPremium" size="small">
            <el-radio-button label="all">全部</el-radio-button>
            <el-radio-button label="premium">溢价</el-radio-button>
            <el-radio-button label="discount">折价</el-radio-button>
          </el-radio-group>
        </div>
        <div v-if="tab === 'lof'" class="filter-group">
          <span class="filter-label">拖拉机</span>
          <el-radio-group v-model="filterIstuo" size="small">
            <el-radio-button label="all">全部</el-radio-button>
            <el-radio-button label="tractor">一拖六</el-radio-button>
            <el-radio-button label="otc">仅场外</el-radio-button>
          </el-radio-group>
        </div>
        <el-button
          v-if="filterActive"
          type="text"
          size="small"
          class="filter-reset"
          @click="resetFilters"
        >
          重置
        </el-button>
        <span class="filter-count">
          {{ filteredRows.length }}/{{ rows.length }} 条
        </span>
      </div>

      <el-table
        :data="filteredRows"
        stripe
        border
        style="width: 100%"
        v-loading="loading"
      >
        <el-table-column
          v-for="col in columns"
          :key="col.key"
          :prop="col.key"
          :align="col.align || 'left'"
          :min-width="col.flex * 72"
        >
          <template slot="header">
            <div v-if="col.key === 'allotRatio'" class="col-header-with-hint">
              <span>{{ col.label }}</span>
              <span class="col-header-hint">登记日收盘持有正股</span>
            </div>
            <div v-else-if="col.key === 'minAllotShares'" class="col-header-with-hint">
              <span>{{ col.label }}</span>
              <span class="col-header-hint">沪10张/深1张面值测算</span>
            </div>
            <div v-else-if="col.key === 'priceToTransfer'" class="col-header-with-hint">
              <span>{{ col.label }}</span>
              <span class="col-header-hint">集思录/现价÷转股价</span>
            </div>
            <span v-else>{{ col.label }}</span>
          </template>
          <template slot-scope="scope">
            <div v-if="col.type === 'fund'" class="fund-info">
              <span class="fund-name">{{ scope.row.name }}</span>
              <span class="fund-code">{{ scope.row.code }}</span>
              <el-tag
                :type="fundTagElType(scope.row.tagType)"
                size="mini"
                class="fund-tag"
              >
                {{ scope.row.tag }}
              </el-tag>
              <el-button
                v-if="showHistory"
                type="text"
                size="mini"
                class="history-btn"
                @click.stop="handleViewHistory(scope.row)"
              >
                查看历史
              </el-button>
            </div>
            <span
              v-else-if="col.type === 'premium'"
              :class="['premium-value', scope.row.premiumClass]"
            >
              {{ scope.row.premium }}
            </span>
            <span
              v-else-if="col.key === 'status'"
              :class="['cell-text', scope.row.status === '待发' ? 'status-pending' : 'status-ended']"
            >
              {{ scope.row.status }}
            </span>
            <span
              v-else
              :class="['cell-text', col.className, col.key === 'codeGuide' ? 'cell-guide' : '']"
            >
              {{ scope.row[col.key] }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <div class="data-note">数据仅供参考，请以交易所及上市公司公告为准</div>
    </div>

    <el-dialog
      :title="historyTitle"
      :visible.sync="historyDialogVisible"
      width="92%"
      top="5vh"
      custom-class="history-dialog"
      append-to-body
      @opened="handleHistoryDialogOpened"
    >
      <div v-loading="historyLoading">
        <HistoryPremiumChart
          v-if="historyRecords.length"
          ref="premiumChart"
          :records="historyRecords"
        />

        <el-table
          v-if="historyRecords.length"
          :data="historyRecords"
          size="mini"
          border
          stripe
          max-height="360"
          style="width: 100%; margin-top: 12px;"
        >
          <el-table-column prop="price_dt" label="日期" width="110" />
          <el-table-column prop="price" label="场内价" align="right" />
          <el-table-column prop="net_value" label="净值" align="right" />
          <el-table-column label="溢价率" align="right" width="90">
            <template slot-scope="scope">
              <span :class="premiumClass(scope.row.discount_rt)">
                {{ formatPremium(scope.row.discount_rt) }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else-if="!historyLoading" description="暂无历史溢价数据" />
        <div class="history-note">数据来源：集思录历史溢价，仅供参考</div>
      </div>
    </el-dialog>

    <el-alert
      v-if="!tableTips || !tableTips.length"
      title="温馨提示"
      type="warning"
      :closable="false"
      style="margin-top: 20px"
    >
      <div class="warning-list">
        <p>1. 数据仅供参考，不构成投资建议！</p>
        <p>2. 数据有一定的延时性，请关注更新时间！</p>
        <p>3. 估值可能会有一定的误差，请谨慎参考！</p>
      </div>
    </el-alert>
  </div>
</template>

<script>
import { fetchLofHistory } from '@/api/fundApi'
import HistoryPremiumChart from '@/components/HistoryPremiumChart.vue'

export default {
  name: 'FundTable',
  components: {
    HistoryPremiumChart
  },
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    tableTips: {
      type: Array,
      default: () => []
    },
    showHistory: {
      type: Boolean,
      default: false
    },
    tab: {
      type: String,
      default: 'lof'
    }
  },
  data() {
    return {
      historyDialogVisible: false,
      historyLoading: false,
      historyTitle: '历史溢价',
      historyRecords: [],
      filterKeyword: '',
      filterTagTypes: ['limit', 'open', 'pause'],
      filterPremium: 'all',
      filterIstuo: 'all'
    }
  },
  computed: {
    showMarketFilter() {
      return this.tab === 'lof' || this.tab === 'etf'
    },
    filteredRows() {
      if (!this.showMarketFilter) {
        return this.rows
      }
      return this.rows.filter((row) => this.matchFilter(row))
    },
    filterActive() {
      return !!this.filterKeyword.trim()
        || this.filterTagTypes.length !== 3
        || this.filterPremium !== 'all'
        || (this.tab === 'lof' && this.filterIstuo !== 'all')
    }
  },
  watch: {
    tab() {
      this.resetFilters()
    }
  },
  methods: {
    resetFilters() {
      this.filterKeyword = ''
      this.filterTagTypes = ['limit', 'open', 'pause']
      this.filterPremium = 'all'
      this.filterIstuo = 'all'
    },
    matchFilter(row) {
      const keyword = this.filterKeyword.trim().toLowerCase()
      if (keyword) {
        const name = String(row.name || '').toLowerCase()
        const code = String(row.code || '').toLowerCase()
        if (!name.includes(keyword) && !code.includes(keyword)) {
          return false
        }
      }

      if (this.tab === 'lof') {
        const tagType = row.tagType || 'limit'
        if (!this.filterTagTypes.includes(tagType)) {
          return false
        }

        const istuo = String(row.istuo || '')
        if (this.filterIstuo === 'tractor' && !istuo.includes('一拖六')) {
          return false
        }
        if (this.filterIstuo === 'otc' && !istuo.includes('仅场外')) {
          return false
        }
      }

      if (this.filterPremium !== 'all') {
        const premium = this.parsePremiumNumber(row.premium)
        if (this.filterPremium === 'premium' && premium <= 0) {
          return false
        }
        if (this.filterPremium === 'discount' && premium >= 0) {
          return false
        }
      }

      return true
    },
    parsePremiumNumber(val) {
      if (val === null || val === undefined || val === '') {
        return 0
      }
      const num = Number(String(val).replace('%', '').trim())
      return Number.isFinite(num) ? num : 0
    },
    async handleViewHistory(row) {
      if (!row || !row.code) {
        return
      }
      this.historyDialogVisible = true
      this.historyLoading = true
      this.historyTitle = `${row.name || ''} (${row.code}) 历史溢价`
      this.historyRecords = []
      try {
        const data = await fetchLofHistory(row.code, true)
        const records = Array.isArray(data.records) ? data.records.slice() : []
        this.historyRecords = records.reverse()
      } catch (error) {
        this.$message.error(error.message || '历史数据加载失败')
      } finally {
        this.historyLoading = false
        this.$nextTick(() => {
          this.resizePremiumChart()
        })
      }
    },
    handleHistoryDialogOpened() {
      this.$nextTick(() => {
        this.resizePremiumChart()
      })
    },
    resizePremiumChart() {
      if (this.$refs.premiumChart) {
        this.$refs.premiumChart.resize()
      }
    },
    formatPremium(val) {
      if (val === null || val === undefined || val === '' || val === '-') {
        return '--'
      }
      const num = Number(val)
      if (!Number.isFinite(num)) {
        return String(val)
      }
      return `${num}%`
    },
    premiumClass(val) {
      const num = Number(val)
      if (!Number.isFinite(num)) {
        return ''
      }
      return num < 0 ? 'premium-negative' : 'premium-positive'
    },
    fundTagElType(tagType) {
      if (tagType === 'pause') {
        return 'info'
      }
      if (tagType === 'open') {
        return 'primary'
      }
      return 'success'
    }
  }
}
</script>

<style scoped>
.cb-tips {
  background: #fff8e6;
  border: 1px solid #ffe0a3;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.cb-tip-item {
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 1.55;
  color: #8a6a00;
}

.cb-tip-item:last-child {
  margin-bottom: 0;
}

.table-container {
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
}

.filter-keyword {
  width: 160px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-label {
  font-size: 12px;
  color: #666;
  flex-shrink: 0;
}

.filter-checks {
  display: flex;
  flex-wrap: wrap;
}

.filter-checks >>> .el-checkbox {
  margin-right: 10px;
}

.filter-checks >>> .el-checkbox__label {
  font-size: 12px;
  padding-left: 6px;
}

.filter-reset {
  color: #ff6f2f;
  padding: 0;
}

.filter-count {
  margin-left: auto;
  font-size: 12px;
  color: #999;
}

.fund-info {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  min-width: 0;
}

.fund-info .fund-name {
  font-size: 13px;
  color: #222;
  font-weight: 600;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fund-code {
  font-size: 13px;
  color: #5a7bb5;
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap;
}

.fund-tag {
  flex-shrink: 0;
}

.history-btn {
  padding: 0;
  margin: 0;
  font-size: 11px;
  color: #ff6f2f;
  flex-shrink: 0;
}

.cell-text {
  font-size: 12px;
  color: #333;
}

.cell-text.col-record-highlight {
  color: #c45656;
  font-weight: 700;
  font-size: 13px;
}

.cell-text.col-guide {
  color: #606266;
  font-size: 11px;
  line-height: 1.4;
}

.cell-text.col-code {
  font-weight: 600;
  color: #303133;
}

.status-pending {
  color: #e6a23c;
  font-weight: 700;
}

.status-ended {
  color: #909399;
}

.col-header-with-hint {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.col-header-hint {
  font-size: 10px;
  color: #000000;
  font-weight: 700;
  margin-top: 2px;
}

.premium-value {
  color: #ff5b41;
  font-weight: 600;
  font-size: 13px;
}

.premium-value.negative {
  color: #18a85a;
}

.premium-positive {
  color: #ff5b41;
  font-weight: 600;
}

.premium-negative {
  color: #18a85a;
  font-weight: 600;
}

.data-note {
  text-align: center;
  color: #b0b3bd;
  font-size: 11px;
  padding: 12px 0;
  margin-top: 8px;
}

.warning-list p {
  margin: 6px 0;
  line-height: 1.6;
  font-size: 12px;
}

.history-note {
  margin-top: 10px;
  font-size: 11px;
  color: #999;
  text-align: center;
}
</style>

<style>
.history-dialog {
  max-width: 720px;
}
</style>
